import type { SupabaseClient } from '@supabase/supabase-js';

import type { Database } from '@/integrations/supabase/types';
import { supabaseRpcWithRetry } from '@/lib/networkRetry';

const PENDING_INVITATION_CLAIM_STORAGE_KEY = 'pendingInvitationClaim';
const INVITE_EMAIL_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export interface PendingInvitationClaim {
  code: string;
  createdAt: string;
}

export interface ParsedInviteEmails {
  emails: string[];
  invalidEmails: string[];
  duplicateCount: number;
}

export interface ExportableInvite {
  code: string;
  invitedEmail?: string | null;
  companyName?: string | null;
  expiresAt?: string | null;
  notes?: string | null;
}

export interface ClaimInvitationResult {
  success: boolean;
  company_id: string | null;
  company_display_name: string | null;
  error_message: string | null;
}

function canUseBrowserStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function normalizeInviteEmail(email: string): string {
  return email.trim().toLowerCase();
}

function escapeCsvValue(value: string): string {
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }

  return value;
}

export function parseInviteEmails(input: string): ParsedInviteEmails {
  const tokens = input
    .split(/[\n,;]+/g)
    .map((value) => normalizeInviteEmail(value))
    .filter(Boolean);

  const uniqueEmails = new Set<string>();
  const invalidEmails = new Set<string>();
  let duplicateCount = 0;

  for (const token of tokens) {
    if (!INVITE_EMAIL_PATTERN.test(token)) {
      invalidEmails.add(token);
      continue;
    }

    if (uniqueEmails.has(token)) {
      duplicateCount += 1;
      continue;
    }

    uniqueEmails.add(token);
  }

  return {
    emails: Array.from(uniqueEmails),
    invalidEmails: Array.from(invalidEmails),
    duplicateCount,
  };
}

export function buildCompanyInviteLink(origin: string, code: string): string {
  const url = new URL('/auth', origin);
  url.searchParams.set('invite', code.trim().toUpperCase());

  return url.toString();
}

export function buildCompanyInviteCsv(invites: ExportableInvite[], origin: string): string {
  const header = ['invited_email', 'code', 'company_name', 'expires_at', 'notes', 'invite_url'];

  const rows = invites.map((invite) => {
    const invitedEmail = invite.invitedEmail ? normalizeInviteEmail(invite.invitedEmail) : '';
    const inviteUrl = buildCompanyInviteLink(origin, invite.code);

    return [
      invitedEmail,
      invite.code,
      invite.companyName ?? '',
      invite.expiresAt ?? '',
      invite.notes ?? '',
      inviteUrl,
    ]
      .map((value) => escapeCsvValue(value))
      .join(',');
  });

  return [header.join(','), ...rows].join('\n');
}

export function savePendingInvitationClaim(code: string): void {
  if (!canUseBrowserStorage()) return;

  const claim: PendingInvitationClaim = {
    code: code.trim().toUpperCase(),
    createdAt: new Date().toISOString(),
  };

  window.localStorage.setItem(PENDING_INVITATION_CLAIM_STORAGE_KEY, JSON.stringify(claim));
}

export function loadPendingInvitationClaim(): PendingInvitationClaim | null {
  if (!canUseBrowserStorage()) return null;

  const rawValue = window.localStorage.getItem(PENDING_INVITATION_CLAIM_STORAGE_KEY);
  if (!rawValue) return null;

  try {
    const parsed = JSON.parse(rawValue) as PendingInvitationClaim;
    if (!parsed?.code) {
      return null;
    }

    return {
      code: parsed.code.trim().toUpperCase(),
      createdAt: parsed.createdAt ?? new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

export function clearPendingInvitationClaim(): void {
  if (!canUseBrowserStorage()) return;

  window.localStorage.removeItem(PENDING_INVITATION_CLAIM_STORAGE_KEY);
}

export async function claimInvitationCode(
  client: SupabaseClient<Database>,
  code: string,
): Promise<{ result: ClaimInvitationResult | null; error: Error | null }> {
  const { data, error } = await supabaseRpcWithRetry<ClaimInvitationResult[]>(
    client,
    'claim_invitation_code',
    {
      p_code: code.trim().toUpperCase(),
    },
  );

  if (error) {
    return {
      result: null,
      error: new Error(error.message),
    };
  }

  return {
    result: data?.[0] ?? null,
    error: null,
  };
}
